import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/ui/Avatar";
import { Icon } from "@/components/ui/Icon";
import { memoUserToAvatar as toAvatarUser } from "@/lib/memoAvatar";
import { t } from "@/lib/strings";
import type { MemoUser, UserSearchResult } from "@/lib/types";

const chip: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "3px 6px 3px 3px",
  borderRadius: 999,
  border: "1px solid var(--hairline)",
  background: "var(--surface)",
  fontSize: 12,
  color: "var(--ink-1)",
};

function toMemoUser(u: UserSearchResult): MemoUser {
  return {
    id: u.id,
    display_name: u.display_name,
    username: u.username,
    avatar_url: u.avatar_url,
    department_workspace_id: u.department_workspace_id ?? null,
    department_color: u.department_color ?? null,
  };
}

export function UserMultiSelect({
  value,
  onChange,
  placeholder,
}: {
  value: MemoUser[];
  onChange: (v: MemoUser[]) => void;
  placeholder?: string;
}) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState<MemoUser[]>([]);

  useEffect(() => {
    const h = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    if (debounced.length < 2) {
      setResults([]);
      return;
    }
    let live = true;
    void api.searchUsers(debounced).then((r) => {
      if (live) setResults(r.map(toMemoUser));
    });
    return () => {
      live = false;
    };
  }, [debounced]);

  const has = new Set(value.map((u) => u.id));
  const add = (u: MemoUser) => {
    onChange([...value, u]);
    setQ("");
    setResults([]);
  };
  const remove = (id: string) => onChange(value.filter((u) => u.id !== id));

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
        {value.map((u) => {
          const name = u.display_name ?? u.username ?? "";
          return (
            <span key={u.id} style={chip}>
              <Avatar user={toAvatarUser(u)} size={18} />
              {name}
              <button
                type="button"
                aria-label={`${name} entfernen`}
                onClick={() => remove(u.id)}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  display: "flex",
                }}
              >
                <Icon name="x" size={13} color="var(--ink-3)" />
              </button>
            </span>
          );
        })}
      </div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder ?? t.assignSearchPlaceholder}
        style={{
          width: "100%",
          fontSize: 13,
          padding: "8px 10px",
          borderRadius: 8,
          border: "1px solid var(--hairline)",
          background: "var(--surface)",
          color: "var(--ink-1)",
          boxSizing: "border-box",
        }}
      />
      {results.length > 0 ? (
        <div
          style={{
            marginTop: 6,
            border: "1px solid var(--hairline)",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {results
            .filter((u) => !has.has(u.id))
            .map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => add(u)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "7px 10px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "var(--ink-1)",
                  fontSize: 13,
                  textAlign: "left",
                }}
              >
                <Avatar user={toAvatarUser(u)} size={20} />
                <span>{u.display_name ?? u.username}</span>
                <span style={{ color: "var(--ink-4)", fontSize: 11, marginLeft: "auto" }}>
                  {u.username}
                </span>
              </button>
            ))}
        </div>
      ) : null}
    </div>
  );
}
